/* eslint @typescript-eslint/no-explicit-any: 0 */
import { createContext, Dispatch, SetStateAction, useMemo, useState } from 'react';

import { curryCognitoCreds } from 'lib/cognito';
import { listS3Items, viewS3Items, uploadS3Item, deleteS3Item } from 'lib/bucket';

export type S3Item = {
  id: number;
  key: string;
  url: string;
};

type BucketProviderProps = {
  children: React.ReactNode;
};

export type Bucket = {
  listItems: any;
  urls: S3Item[];
  setUrls: Dispatch<SetStateAction<S3Item[]>>;
  getListItems: (prefix: string, asPromise?: boolean) => any;
  viewItems: (key: string[]) => void;
  sendItem: (img: Array<FileList | null>, path: string) => any;
  deleteItem: (key: string) => any;
  photosFromInput: FileList[] | null;
  setPhotosFromInput: any;
  totalAddedPhotosCount: number;
};

const defaultState: Bucket = {
  listItems: [],
  urls: [],
  setUrls: () => null,
  getListItems: () => null,
  viewItems: () => null,
  sendItem: () => null,
  deleteItem: () => null,
  photosFromInput: null,
  setPhotosFromInput: () => null,
  totalAddedPhotosCount: 0,
};

export const BucketContext = createContext(defaultState);

export const BucketProvider: React.FunctionComponent<BucketProviderProps> = ({ children }) => {
  const [listItems, listItemsSet] = useState<string[]>([]);
  const [urls, setUrls] = useState<S3Item[]>([]);
  const [photosFromInput, setPhotosFromInput] = useState<any[] | null>(null);
  const totalAddedPhotosCount = urls.length;

  async function sendItem(img: Array<FileList | null>, path: string) {
    const withCognitoCreds = await curryCognitoCreds();
    const upload = withCognitoCreds(uploadS3Item);

    return upload(img, path);
  }

  async function viewItems(keys: string[]) {
    setUrls([]);

    const withCognitoCreds = await curryCognitoCreds();
    const view = withCognitoCreds(viewS3Items);

    Promise.all(keys?.map((key: string) => view(key))).then((res) => {
      if (res) {
        const resIntoObject = res?.map((item, index) => ({
          id: index,
          key: item.key,
          url: item.url,
        }));

        setUrls(resIntoObject);
      }
    });
  }

  async function getListItems(prefix: string) {
    setUrls([]);

    const withCognitoCreds = await curryCognitoCreds();
    const list = withCognitoCreds(listS3Items);

    return list(prefix).then((res: any) => {
      if (res && res.Contents) {
        const resMapped = res?.Contents?.map((item: any) => item.Key);
        listItemsSet(resMapped);
        viewItems(resMapped);
      }
    });
  }

  async function deleteItem(key: string) {
    const withCognitoCreds = await curryCognitoCreds();
    const remove = withCognitoCreds(deleteS3Item);

    return remove(key);
  }

  const state: Bucket = useMemo(
    () => ({
      listItems,
      getListItems,
      viewItems,
      sendItem,
      urls,
      setUrls,
      deleteItem,
      photosFromInput,
      setPhotosFromInput,
      totalAddedPhotosCount,
    }),
    [listItems, urls, photosFromInput, totalAddedPhotosCount]
  );

  return <BucketContext.Provider value={state}>{children}</BucketContext.Provider>;
};
